
import React from 'react';
import { Link } from 'react-router-dom';
import { ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface FooterProps {
  user: any;
}

const Footer: React.FC<FooterProps> = ({ user }) => {
  return (
    <footer className="bg-techne-purple text-white">
      {/* CTA Strip */}
      <div className="border-b border-white/20">
        <div className="container-custom py-12">
          <div className="text-center max-w-3xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-bold mb-6">
              Ready to strengthen AI oversight?
            </h2>
            <div className="flex flex-col sm:flex-row gap-3 justify-center items-center">
              <Button 
                asChild 
                size="lg" 
                className="bg-white text-techne-purple hover:bg-gray-100 w-full sm:w-auto"
              >
                <Link to="/contact#briefing">
                  Book a 25‑Minute Board AI Oversight Briefing
                </Link>
              </Button>
              <Button 
                asChild 
                size="lg" 
                variant="outline" 
                className="border-white text-white hover:bg-white/10 w-full sm:w-auto"
              >
                <Link to="/quiz">
                  Take the Board AI Oversight Assessment
                </Link>
              </Button>
              <Button 
                asChild 
                size="lg" 
                variant="ghost" 
                className="text-white hover:bg-white/10 underline underline-offset-4 w-full sm:w-auto"
              >
                <Link to="/blueprint#pricing">
                  Enroll in the Intensive
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Footer Links */}
      <div className="container-custom py-10">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-sm">
          {/* Programs Column */}
          <div>
            <h3 className="font-semibold text-base mb-4">Programs</h3>
            <ul className="space-y-2">
              <li>
                <Link to="/advisory" className="text-gray-300 hover:text-white transition-colors">
                  Advisory for Boards
                </Link>
              </li>
              <li>
                <Link to="/blueprint" className="text-gray-300 hover:text-white transition-colors">
                  Director AI Oversight Intensive
                </Link>
              </li>
              <li>
              <Link to="/subscription" className="text-gray-300 hover:text-white transition-colors">
                  Continuous
                </Link>
              </li>
            </ul>
          </div>

          {/* Start Here Column */}
          <div>
            <h3 className="font-semibold text-base mb-4">Start Here</h3>
            <ul className="space-y-2">
              <li>
                <Link to="/quiz" className="text-gray-300 hover:text-white transition-colors">
                  Assessment
                </Link>
              </li>
              <li>
                <Link to="/insights" className="text-gray-300 hover:text-white transition-colors">
                  Insights
                </Link>
              </li>
            </ul>
          </div>

          {/* Company Column */}
          <div>
            <h3 className="font-semibold text-base mb-4">Company</h3>
            <ul className="space-y-2">
              <li>
                <Link to="/contact" className="text-gray-300 hover:text-white transition-colors">
                  Contact
                </Link>
              </li>
              <li>
                <a 
                  href="https://techne.ai/privacy-policy" 
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-300 hover:text-white transition-colors flex items-center gap-1"
                >
                  Terms <ExternalLink size={12} />
                </a>
              </li>
              <li>
                <a 
                  href="https://techne.ai/privacy-policy" 
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-300 hover:text-white transition-colors flex items-center gap-1"
                >
                  Privacy <ExternalLink size={12} />
                </a>
              </li>
              <li>
                <Link to="/login" className="text-gray-300 hover:text-white transition-colors">
                  Log in
                </Link>
              </li>
            </ul>
          </div>
        </div>

        {/* Disclaimer */}
        <div className="mt-10 pt-6 border-t border-white/20">
          <p className="text-xs text-gray-400 max-w-3xl">
            BoardSight provides governance education and advisory support designed for board oversight. 
            BoardSight does not provide legal advice. For jurisdiction-specific legal interpretation, 
            consult qualified counsel.
          </p>
        </div>

        {/* Copyright */}
        <div className="mt-4 text-center text-sm text-gray-400">
          <p>© {new Date().getFullYear()} BoardSight. All rights reserved.</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
